import { forwardRef, useRef, useCallback, type ReactNode, type HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { ScrollContainerProvider } from "./ScrollContainerContext";
import { ScrollAnchor, type ScrollAnchorProps } from "./ScrollAnchor";

export interface ListProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  /** Extra scroll triggers (e.g. todos). Changes to this array trigger scroll. */
  scrollTriggers?: ScrollAnchorProps["scrollTriggers"];
  /** Scroll behavior. @default "smooth" */
  scrollBehavior?: ScrollAnchorProps["behavior"];
  /** Disable auto-scroll entirely. */
  scrollDisabled?: ScrollAnchorProps["disabled"];
  /** Minimum ms between auto-scrolls during streaming. */
  scrollThrottleMs?: ScrollAnchorProps["throttleMs"];
}

export const List = forwardRef<HTMLDivElement, ListProps>(
  ({ children, className, scrollTriggers, scrollBehavior, scrollDisabled, scrollThrottleMs, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLDivElement>(null);

    const mergedRef = useCallback(
      (node: HTMLDivElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef]
    );

    return (
      <ScrollContainerProvider value={internalRef}>
        <div
          ref={mergedRef}
          role="log"
          data-testid="message-list"
          className={twMerge(
            "flex flex-col overflow-y-auto",
            "[&>*+*]:mt-5 [&>[data-role=assistant]+[data-role=assistant]]:mt-2",
            className
          )}
          {...props}
        >
          {children}
          <ScrollAnchor
            behavior={scrollBehavior}
            disabled={scrollDisabled}
            throttleMs={scrollThrottleMs}
            scrollTriggers={scrollTriggers}
          />
        </div>
      </ScrollContainerProvider>
    );
  }
);

List.displayName = "Chat.Messages.List";
